<template>
  <div class="pay-plan-version-pane">
    <div class="ppvp__panel">
      <!-- Header -->
      <header class="ppvp__header">
        <div class="ppvp__header-bar">
          <div class="ppvp__header-main">
            <div class="ppvp__identity">
              <v-avatar size="32" class="ppvp__avatar">
                <img
                  v-if="version.avatar_url"
                  :src="version.avatar_url"
                  :alt="version.employee"
                />
                <span v-else class="ppvp__avatar-fallback">{{ employeeInitials }}</span>
              </v-avatar>
              <span class="ppvp__employee-name">{{ version.employee }}</span>
              <span v-if="version.employee_title" class="ppvp__employee-title">
                {{ version.employee_title }}
              </span>
            </div>
            <div v-if="scenario !== 'loading'" class="ppvp__header-meta">
              <VersionBadge :code="versionChipText" />
              <PayPlanLifecycleBadge
                v-if="scenario === 'future-scheduled'"
                status="In Force"
              />
              <PayPlanPeriodStateBadge
                v-if="scenario === 'future-scheduled'"
                state="Future"
                filled
              />
              <PayPlanPeriodStateBadge
                v-if="scenario === 'voided'"
                state="Voided"
                filled
              />
              <span class="ppvp__period-text">{{ headerPeriodText }}</span>
            </div>
          </div>
          <div class="ppvp__header-actions">
            <ChatterIcon @click="$emit('open-chatter')" />
            <button
              type="button"
              class="ppvp__icon-btn"
              aria-label="Close"
              @click="$emit('close')"
            >
              <v-icon size="20">mdi-close</v-icon>
            </button>
          </div>
        </div>
        <div class="ppvp__period-bar-wrap">
          <div v-if="scenario === 'loading'" class="ppvp__period-bar-skeleton"></div>
          <PeriodBar
            v-else
            :state="periodBarState"
            :label="periodLabel"
            :open-ended="isOpenEnded"
            :selectable="false"
          />
        </div>
      </header>

      <!-- Body -->
      <div v-if="scenario === 'loading'" class="ppvp__body">
        <div class="ppvp__body-skeleton">
          <div class="ppvp__skeleton-line ppvp__skeleton-line--long"></div>
          <div class="ppvp__skeleton-line ppvp__skeleton-line--medium"></div>
          <div class="ppvp__skeleton-line ppvp__skeleton-line--short"></div>
          <div class="ppvp__skeleton-line ppvp__skeleton-line--long"></div>
          <div class="ppvp__skeleton-line ppvp__skeleton-line--medium"></div>
        </div>
      </div>
      <div v-else class="ppvp__body">
        <!-- Version alert banner -->
        <div class="ppvp__info-banner" role="status">
          <div class="ppvp__info-banner-icon" aria-hidden="true">
            <v-icon size="20" color="#ffffff">{{ bannerIcon }}</v-icon>
          </div>
          <p class="ppvp__info-banner-text">{{ bannerText }}</p>
        </div>

        <!-- Actions -->
        <div v-if="scenario === 'future-scheduled'" class="ppvp__actions-row">
          <EXTooltip
            :disabled="!isProposeChangesDisabled"
            placement="top"
            :title="proposeChangesNote"
          >
            <StandardButton
              type="magenta"
              variant="secondary"
              size="small"
              :disabled="isProposeChangesDisabled"
              @click="$emit('propose-changes')"
            >
              Propose Changes
            </StandardButton>
          </EXTooltip>
          <EXTooltip
            :disabled="voidable"
            placement="top"
            :title="voidBlockedNote"
          >
            <StandardButton
              type="red"
              variant="secondary"
              size="small"
              :disabled="!voidable"
              @click="$emit('void')"
            >
              Void
            </StandardButton>
          </EXTooltip>
        </div>

        <!-- Country & currency -->
        <div class="ppvp__field-row">
          <div class="ppvp__field-col">
            <label class="ppvp__field-label">Country</label>
            <div class="ppvp__field-readonly" :class="{ 'ppvp__field-readonly--voided': scenario === 'voided' }">
              <span>{{ version.country || '—' }}</span>
              <v-icon size="20" class="ppvp__lock-icon">mdi-lock</v-icon>
            </div>
          </div>
          <div class="ppvp__field-col">
            <label class="ppvp__field-label">Currency</label>
            <div class="ppvp__field-readonly" :class="{ 'ppvp__field-readonly--voided': scenario === 'voided' }">
              <span>{{ version.currency || '—' }}</span>
              <v-icon size="20" class="ppvp__lock-icon">mdi-lock</v-icon>
            </div>
          </div>
        </div>

        <!-- Pay terms: amount, FTE, pay basis -->
        <div class="ppvp__field-row ppvp__field-row--pay-terms">
          <div class="ppvp__field-col ppvp__field-col--amount">
            <CurrencyAmountInput
              label="Base Salary"
              :model-value="version.amount"
              :locked="true"
              :class="{ 'ppvp__field--voided': scenario === 'voided' }"
            />
          </div>
          <div class="ppvp__field-col">
            <label class="ppvp__field-label">
              FTE
              <EXTooltip placement="top" title="From primary assignment">
                <v-icon small class="ppvp__help-icon">mdi-help-circle-outline</v-icon>
              </EXTooltip>
            </label>
            <div class="ppvp__field-readonly" :class="{ 'ppvp__field-readonly--voided': scenario === 'voided' }">
              <span>{{ fteDisplayLabel }}</span>
              <v-icon size="20" class="ppvp__lock-icon">mdi-lock</v-icon>
            </div>
          </div>
          <div class="ppvp__field-col ppvp__field-col--basis">
            <ExpressedAs
              :model-value="version.pay_basis"
              label="Pay Basis"
              :locked="true"
              :class="{ 'ppvp__field--voided': scenario === 'voided' }"/>
          </div>
        </div>

        <!-- Fair Pay section -->
        <div v-if="scenario === 'voided' && fairPayAtEffectiveDate" class="ppvp__fair-pay-stored">
          <span class="ppvp__fair-pay-stored-text" :class="{ 'ppvp__field-readonly--voided': true }">
            {{ fairPayStoredText }}
          </span>
        </div>
        <div v-else-if="scenario === 'future-scheduled'" class="ppvp__fair-pay-section">
          <FairPayCalculatorAtEffectiveDateCard
            v-if="readinessEnabled && fairPayAtEffectiveDate"
            class="ppvp__calculator"
            calculator="read-only"
            :as-of-date="readinessAsOfDate"
            :position="fairPayAtEffectiveDate.position"
            :pay-range-version="fairPayAtEffectiveDate.pay_range_version"
            :pay-range-adjusted="fairPayAtEffectiveDate.pay_range_adjusted"
            :fte-adjustment-applied="fairPayAtEffectiveDate.fte_adjusted"
            :current-readiness="fairPayAtEffectiveDate.readiness"
            :fair-at-current="fairPayAtEffectiveDate.fair_at_current"
          />
          <div
            v-else-if="!readinessEnabled"
            class="ppvp__fair-pay-unavailable"
          >
            <p class="ppvp__fair-pay-unavailable-text">
              Fair Pay calculation is not available
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue";
import ChatterIcon from "@components/ChatterIcon/ChatterIcon.vue";
import CurrencyAmountInput from "@components/CurrencyAmountInput/CurrencyAmountInput.vue";
import PeriodBar from "@components/PeriodBar/PeriodBar.vue";
import EXTooltip from "@components/tooltip/EXTooltip.vue";
import VersionBadge from "@components/VersionBadge/VersionBadge.vue";
import PayPlanLifecycleBadge from "@components/PayPlanLifecycleBadge/PayPlanLifecycleBadge.vue";
import PayPlanPeriodStateBadge from "@components/PayPlanPeriodStateBadge/PayPlanPeriodStateBadge.vue";
import StandardButton from "@components/StandardButton/StandardButton.vue";
import ExpressedAs from "@components/ExpressedAs/ExpressedAs.vue";
import FairPayCalculatorAtEffectiveDateCard from "@components/FairPayCalculatorAtEffectiveDateCard/FairPayCalculatorAtEffectiveDateCard.vue";
import { formatAssignmentFte } from "@components/EmployeeToPrimaryAssignmentBar/employeeToPrimaryAssignmentBarUtils";
import { formatFairPayDate } from "@components/utils/fairPayFormat";

const props = defineProps({
  scenario: {
    type: String,
    required: true,
    validator: (value) => ["loading", "future-scheduled", "voided"].includes(value),
  },
  version: {
    type: Object,
    required: true,
  },
  fairPayAtEffectiveDate: {
    type: Object,
    default: null,
  },
  readinessEnabled: {
    type: Boolean,
    default: true,
  },
  voidable: {
    type: Boolean,
    default: false,
  },
  voidBlockedBy: {
    type: String,
    default: "",
  },
  voidReason: {
    type: String,
    default: "",
  },
});

const emit = defineEmits(["void", "open-chatter", "close", "propose-changes"]);

const employeeInitials = computed(() => {
  if (!props.version.employee) return "";
  const names = props.version.employee.trim().split(/\s+/);
  if (names.length === 1) return names[0].charAt(0).toUpperCase();
  return (names[0].charAt(0) + names[names.length - 1].charAt(0)).toUpperCase();
});

const versionChipText = computed(() => {
  return props.version.code || "";
});

const headerPeriodText = computed(() => {
  if (props.scenario === "loading") return "";
  if (!props.version.effective_from) return "";
  const from = formatFairPayDate(props.version.effective_from);
  if (props.version.effective_to) {
    return `${from} – ${formatFairPayDate(props.version.effective_to)}`;
  }
  return `From ${from}`;
});

const periodBarState = computed(() => {
  if (props.scenario === "future-scheduled") return "Future";
  if (props.scenario === "voided") return "Current"; // Current state renders green colors
  return "In Force";
});

const periodLabel = computed(() => {
  return headerPeriodText.value;
});

const isOpenEnded = computed(() => {
  return !props.version.effective_to;
});

const bannerIcon = computed(() => {
  if (props.scenario === "future-scheduled") return "mdi-information-outline";
  if (props.scenario === "voided") return "mdi-alert-outline";
  return "mdi-information-outline";
});

const bannerText = computed(() => {
  if (props.scenario === "future-scheduled" && props.version.effective_from) {
    return `This version takes effect on ${formatFairPayDate(props.version.effective_from)}`;
  }
  if (props.scenario === "voided") {
    return "This version was voided and was never valid — see chatter for the reason";
  }
  return "";
});

const isProposeChangesDisabled = computed(() => {
  return true; // Always disabled per spec: "Only one future pay plan is allowed"
});

const proposeChangesNote = computed(() => {
  if (props.voidBlockedBy) {
    return `Only one future pay plan is allowed. Void ${props.voidBlockedBy} first to propose a different change.`;
  }
  return "Only one future pay plan is allowed. Void this version first to propose a different change.";
});

/**
 * Rule A-9: versions are voided newest first, so a version with a newer
 * non-voided row above it cannot be voided until that one is. The tooltip names
 * it (mirrors the Lapsed drawer).
 */
const voidBlockedNote = computed(() => {
  if (props.voidBlockedBy) {
    return `Void ${props.voidBlockedBy} first — versions are voided newest first`;
  }
  return "Versions are voided newest first";
});

const fteDisplayLabel = computed(() => {
  if (props.version.fte == null) return "—";
  return formatAssignmentFte(props.version.fte);
});

const readinessAsOfDate = computed(() => {
  if (!props.fairPayAtEffectiveDate?.readinessAsOf) return "";
  return formatFairPayDate(props.fairPayAtEffectiveDate.readinessAsOf);
});

const fairPayStoredText = computed(() => {
  if (!props.fairPayAtEffectiveDate) return "";
  const readiness = props.fairPayAtEffectiveDate.readiness || 0;
  const date = readinessAsOfDate.value;
  return `Fairly paid at ${readiness}% readiness on ${date}`;
});

</script>

<style scoped>
.pay-plan-version-pane {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #f9fafb;
}

.ppvp__panel {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #ffffff;
  border: 1px solid #eaecf0;
  border-radius: 5px;
}

/* Header */
.ppvp__header {
  display: flex;
  flex-direction: column;
  gap: 11px;
  padding: 0 24px;
  background: #ffffff;
  border-bottom: 1px solid #eaecf0;
}

.ppvp__header-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 20px;
}

.ppvp__header-main {
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1;
}

.ppvp__identity {
  display: flex;
  align-items: center;
  gap: 12px;
}

.ppvp__avatar {
  flex-shrink: 0;
}

.ppvp__avatar-fallback {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  font-size: 14px;
  font-weight: 600;
  color: #ffffff;
  background: #7f56d9;
}

.ppvp__employee-name {
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
  color: #101828;
}

.ppvp__employee-title {
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #475467;
}

.ppvp__header-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.ppvp__period-text {
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #475467;
}

.ppvp__header-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.ppvp__icon-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  padding: 0;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: #475467;
  cursor: pointer;
  transition: background-color 0.2s;
}

.ppvp__icon-btn:hover {
  background: #f9fafb;
}

.ppvp__icon-btn:focus {
  outline: 2px solid #7f56d9;
  outline-offset: 2px;
}

.ppvp__period-bar-wrap {
  padding-bottom: 16px;
}

.ppvp__period-bar-skeleton {
  height: 32px;
  border-radius: 6px;
  background: #eaecf0;
}

/* Body */
.ppvp__body {
  flex: 1;
  overflow-y: auto;
  background: #ffffff;
}

.ppvp__body-skeleton {
  display: flex;
  flex-direction: column;
  gap: 17px;
  padding: 24px;
}

.ppvp__skeleton-line {
  height: 16px;
  border-radius: 4px;
  background: #eaecf0;
}

.ppvp__skeleton-line--long {
  width: 100%;
}

.ppvp__skeleton-line--medium {
  width: 75%;
}

.ppvp__skeleton-line--short {
  width: 50%;
}

/* Info Banner */
.ppvp__info-banner {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  margin: 24px 24px 0;
  padding: 16px;
  border-radius: 12px;
  background: #f9f5ff;
}

.ppvp__info-banner-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: #7f56d9;
  flex-shrink: 0;
}

.ppvp__info-banner-text {
  margin: 10px 0 0;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  color: #344054;
}

/* Actions */
.ppvp__actions-row {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 17px 24px 0;
}

/* Fields */
.ppvp__field-row {
  display: flex;
  flex-wrap: wrap;
  gap: 17px;
  padding: 17px 24px 0;
}

.ppvp__field-row--pay-terms {
  align-items: flex-start;
}

.ppvp__field-col {
  flex: 1 1 280px;
  min-width: 0;
}

.ppvp__field-col--amount {
  flex: 1 1 312px;
}

.ppvp__field-label {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-bottom: 6px;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: #344054;
}

.ppvp__help-icon {
  color: #475467;
}

.ppvp__field-readonly {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  background: #f9fafb;
  font-size: 16px;
  line-height: 24px;
  color: #344054;
}

.ppvp__field-readonly--voided {
  text-decoration: line-through;
  color: #475467;
}

.ppvp__lock-icon {
  color: #98a2b3;
}

.ppvp__field--voided :deep(input),
.ppvp__field--voided :deep(.currency-amount-display) {
  text-decoration: line-through;
  color: #475467;
}

/* Fair Pay */
.ppvp__fair-pay-section {
  padding: 6px 24px 24px;
}

.ppvp__calculator {
  margin-top: 0;
}

.ppvp__fair-pay-stored {
  padding: 17px 24px 0;
}

.ppvp__fair-pay-stored-text {
  display: block;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #475467;
}

.ppvp__fair-pay-unavailable {
  padding: 16px;
  border: 1px solid #eaecf0;
  border-radius: 12px;
  background: #f9fafb;
  text-align: center;
}

.ppvp__fair-pay-unavailable-text {
  margin: 0;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: #475467;
}
</style>
